import { Injectable } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ICONS } from '@shared/data/icons';

export type IconName = keyof typeof ICONS;

@Injectable({ providedIn: 'root' })
export class IconService {
  private loadedIcons = new Set<IconName>();
  private iconCache = new Map<IconName, string>();

  constructor(
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer
  ) {
    // Precargar URLs en caché
    Object.entries(ICONS).forEach(([name, path]) => {
      this.iconCache.set(name as IconName, path);
    });
  }

  /**
   * @name loadIcon
   * @description Carga un ícono SVG específico en el registro si aún no ha sido cargado.
   * @param {IconName} iconName El nombre del ícono a cargar.
   */
  public loadIcon(iconName: IconName): void {
    if (this.loadedIcons.has(iconName)) return;

    const iconPath = this.iconCache.get(iconName);
    if (!iconPath) {
      console.warn(`Icono ${iconName} no encontrado en el registro.`);
      return;
    }

    this.iconRegistry.addSvgIcon(
      iconName,
      this.sanitizer.bypassSecurityTrustResourceUrl(iconPath)
    );
    this.loadedIcons.add(iconName);
  }

  /**
   * @name loadIcons
   * @description Carga una lista de íconos SVG específicos.
   * @param {IconName[]} iconNames Un arreglo con los nombres de los íconos a cargar.
   */
  public loadIcons(iconNames: IconName[]): void {
    iconNames.forEach(name => this.loadIcon(name));
  }

  /**
   * @name loadAllIcons
   * @description Carga todos los íconos SVG disponibles en el caché. Útil para precargar íconos comunes.
   */
  public loadAllIcons(): void {
    Array.from(this.iconCache.keys()).forEach(iconName => {
      this.loadIcon(iconName);
    });
  }

  /**
   * @name isIconLoaded
   * @description Verifica si un ícono específico ya ha sido cargado en el registro.
   * @param {IconName} iconName El nombre del ícono a verificar.
   * @returns {boolean} Retorna `true` si el ícono está cargado, `false` en caso contrario.
   */
  public isIconLoaded(iconName: IconName): boolean {
    return this.loadedIcons.has(iconName);
  }
}